import React from 'react';
import Icon from 'carbon-react/lib/components/icon';

import { WorkspaceMember } from '@futrli-sdk/people';
import { FormattedMessage } from '@futrli/localisation';

import { ListingPageRowLayout } from '@futrli/ui-components/components/listing-page/listing-page-row/listing-page-row-layout/listing-page-row-layout';
import { ListingPageRowNameCell } from '@futrli/ui-components/components/listing-page/listing-page-row/listing-page-row-name-cell/listing-page-row-name-cell';
import { ListingPageRowMetricCell } from '@futrli/ui-components/components/listing-page/listing-page-row/listing-page-row-metric-cell/listing-page-row-metric-cell';
import { ExportedPdfRowItem } from './types';
import { ExportedPdfActionsPopover } from './exported-pdf-actions-popover';
import { useExportedPdfRow } from './hooks/use-exported-pdf-row';

interface ExportedPdfRowProps {
  item: ExportedPdfRowItem;
  members: WorkspaceMember[];
  onDelete: (item: ExportedPdfRowItem) => void;
}

/**
 * Renders a single row in the Exported PDFs tab.
 * Displays the report name (clickable to view PDF), creator info, date/time, repeat frequency,
 * send-to count, and an actions popover menu.
 *
 * @param props.item - The exported or dispatched report row item.
 * @param props.members - Workspace members for creator name resolution.
 * @param props.onDelete - Callback to trigger the delete dialog for this item.
 */
export const ExportedPdfRow = ({ item, members, onDelete }: ExportedPdfRowProps) => {
  const {
    name,
    creatorName,
    creatorLabel,
    creatorSubLabel,
    formattedDate,
    formattedTime,
    repeat,
    sendTo,
    isViewing,
    handleViewPdf
  } = useExportedPdfRow({ item, members });

  return (
    <ListingPageRowLayout
      nameColumn={
        <ListingPageRowNameCell
          name={
            <>
              <Icon type="file_pdf" mr="8px" />
              {name}
            </>
          }
          onNameClick={() => {
            if (!isViewing) {
              handleViewPdf();
            }
          }}
        />
      }
      metrics={
        <>
          <ListingPageRowMetricCell label={creatorLabel} value={creatorName} subLabel={creatorSubLabel} />
          <ListingPageRowMetricCell
            label={<FormattedMessage id="predict.date_created" defaultMessage="Date created" />}
            value={formattedDate}
          />
          <ListingPageRowMetricCell
            label={<FormattedMessage id="predict.time_created" defaultMessage="Time created" />}
            value={formattedTime}
          />
          <ListingPageRowMetricCell
            label={<FormattedMessage id="common.repeat" defaultMessage="Repeat" />}
            value={repeat}
          />
          <ListingPageRowMetricCell
            label={<FormattedMessage id="reporting.sent_to" defaultMessage="Sent to" />}
            value={sendTo}
          />
        </>
      }
      actions={<ExportedPdfActionsPopover item={item} onDelete={() => onDelete(item)} />}
    />
  );
};
